import React, {useEffect, useState} from "react";

import {useTranslation} from "react-i18next";
import Typography from "@mui/material/Typography";
import { Add as AddIcon } from '@vedmarket/ui-kit';
import {COLORS} from "../../../../../../../../../../../config/constants";
import './supplier.scss';
import {useFormContext} from "react-hook-form";
import {INVOICE_FORM, InvoiceForm} from "../editInvoice";
import { Completed } from '@vedmarket/ui-kit';
import MenuButton from '../../../../../../../../../../common/menuButton';
import ModalSupplier from "../supplierModal";
import {useGetSuppliersMutation} from "../../../../../../../../../../../api/dictionary";
import {useLazyInvoicesByOrderIdQuery} from "../../../../../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../../../../../utils/hooks";
import SupplierSelectModal from "./components/supplierSelectModal";
import {getSupplierSummaryInfo} from './summary-info';

const SUPPLIERS_REQUEST_PARAMS: DictionaryParamsInput = {
  ordering: "name",
}

const Supplier = () => {

  const { t } = useTranslation()
  const orderId = useGetCalcIdFromRoute()

  const [ openModal, setOpenModal ] = useState(false);
  const [ openSelectModal, setOpenSelectModal ] = useState(false);
  const [ isEdit, setIsEdit ] = useState(false);
  const [ shouldReturnToSelectionModal, setShouldReturnToSelectionModal ] = useState(false);
  const [ suppliersListRefreshId, setSuppliersListRefreshId ] = useState(0);
  const [ addedSupplierId, setAddedSupplierId ] = useState<number>();
  const [ supplierIdToSelect, setSupplierIdToSelect ] = useState<number>();
  const [ editingSupplier, setEditingSupplier ] = useState<SupplierDictionaryOutput>();
  const [ suppliers, setSuppliers ] = useState<SupplierDictionaryOutput[]>([]);

  const { getValues, setValue } = useFormContext<InvoiceForm>()

  const [ getSuppliers ] = useGetSuppliersMutation()
  const [ triggerGetInvoices ] = useLazyInvoicesByOrderIdQuery();

  const loadSuppliers = async () => {
    const suppliersResponse = await getSuppliers(SUPPLIERS_REQUEST_PARAMS)
      .unwrap()
      .catch((): SuppliersResponse => ({ results: [] }));

    const actualSuppliers = suppliersResponse?.results || [];

    setSuppliers(actualSuppliers);

    return actualSuppliers;
  }

  useEffect(() => {
    loadSuppliers();
  }, [])

  const handleAdd = () => {
    setIsEdit(false);
    setEditingSupplier(undefined);
    setOpenModal(true);
  }

  const handleClose = () => {
    setOpenModal(false);
    setIsEdit(false);
    setEditingSupplier(undefined);

    if (shouldReturnToSelectionModal) {
      setOpenSelectModal(true);
      setShouldReturnToSelectionModal(false);
    }
  }

  const handleOpenEditModal = (supplier?: SupplierDictionaryOutput) => {
    if (!supplier) {
      return;
    }

    setOpenModal(true);
    setIsEdit(true);
    setEditingSupplier(supplier);
  };

  const handleSuccessNewSupplier = async (supplier: SupplierDictionaryOutput) => {
    setOpenModal(false);
    setIsEdit(false);
    setEditingSupplier(undefined);
    setAddedSupplierId(supplier.id);
    setSuppliers((prevSuppliers: SupplierDictionaryOutput[]) => [
      supplier,
      ...prevSuppliers.filter((prevSupplier) => prevSupplier.id !== supplier.id)
    ]);
    await loadSuppliers();

    if (openSelectModal) {
      setSuppliersListRefreshId((prevId: number) => prevId + 1);
    }

    if (shouldReturnToSelectionModal) {
      setSupplierIdToSelect(supplier.id);
      setOpenSelectModal(true);
      setShouldReturnToSelectionModal(false);
      return;
    }

    if (supplier.id) {
      setValue(INVOICE_FORM.SUPPLIER, supplier.id);
    }
  }

  const handleSuccessUpdateSupplier = async () => {
    setOpenModal(false);
    setIsEdit(false);
    setEditingSupplier(undefined);
    await loadSuppliers();
    triggerGetInvoices(orderId);

    if (openSelectModal) {
      setSuppliersListRefreshId((prevId: number) => prevId + 1);
    }

    if (shouldReturnToSelectionModal) {
      setOpenSelectModal(true);
      setShouldReturnToSelectionModal(false);
    }
  }

  const handleOpenSelectModal = async () => {
    const actualSuppliers = await loadSuppliers();

    if (!actualSuppliers.length) {
      setShouldReturnToSelectionModal(false);
      setOpenSelectModal(false);
      handleAdd();
      return;
    }

    setAddedSupplierId(undefined);
    setSupplierIdToSelect(getValues(INVOICE_FORM.SUPPLIER) ? Number(getValues(INVOICE_FORM.SUPPLIER)) : undefined);
    setOpenSelectModal(true);
  }

  const handleRemoveSupplier = () => {
    setValue(INVOICE_FORM.SUPPLIER, '');
  }

  const handleChooseSupplier = (supplierId: number) => {
    setAddedSupplierId(undefined);
    setValue(INVOICE_FORM.SUPPLIER, supplierId);
    setOpenSelectModal(false);
  }

  const handleAddFromSelectionModal = () => {
    setShouldReturnToSelectionModal(true);
    handleAdd();
  }

  const handleEditFromSelectionModal = (supplier: SupplierDictionaryOutput) => {
    handleOpenEditModal(supplier);
  }

  const handleDeleteFromSelectionModal = async (supplierId: number) => {
    if (addedSupplierId === supplierId) {
      setAddedSupplierId(undefined);
    }

    if (supplierIdToSelect === supplierId) {
      setSupplierIdToSelect(undefined);
    }

    if (Number(getValues(INVOICE_FORM.SUPPLIER)) === supplierId) {
      setValue(INVOICE_FORM.SUPPLIER, '');
    }

    setSuppliers((prevSuppliers: SupplierDictionaryOutput[]) => prevSuppliers.filter((supplier) => supplier.id !== supplierId));
    triggerGetInvoices(orderId);
  }

  const hasSupplier = getValues()[INVOICE_FORM.SUPPLIER];

  const currentSupplier = suppliers.find((supplier) => supplier.id === Number(hasSupplier));

  return (
    <div className='invoice-form__supplier supplier'>
      {!currentSupplier && <Typography variant='body1' sx={{
        color: COLORS.BLUE,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: 0,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
      }}
      onClick={handleOpenSelectModal}
      >
        <AddIcon style={{
          marginRight: '8px',
          width: '24px',
          height: '24px',
          flexShrink: 0,
          fill: 'currentColor',
        }}/>
        {t('calc__text__main__addASupplierCompany')}
      </Typography>}
      {currentSupplier && <Completed
        info={getSupplierSummaryInfo({supplier: currentSupplier, t})}
        isFullRow
        hasCollapse
        onEdit={handleOpenSelectModal}
        MenuComponent={MenuButton}
        onRemove={handleRemoveSupplier}
        caption={t('calc__text__main__supplierCompany')}
        isDashed
      />}
      <SupplierSelectModal
        isOpen={openSelectModal}
        addedSupplierId={addedSupplierId}
        refreshId={suppliersListRefreshId}
        selectedSupplierId={supplierIdToSelect}
        suppliers={suppliers}
        onClose={() => {
          setAddedSupplierId(undefined);
          setOpenSelectModal(false);
        }}
        onAddSupplier={handleAddFromSelectionModal}
        onDeleteSupplier={handleDeleteFromSelectionModal}
        onChooseSupplier={handleChooseSupplier}
        onEditSupplier={handleEditFromSelectionModal}
        onSelectSupplier={setSupplierIdToSelect}
      />
      <ModalSupplier
        isOpen={openModal}
        handleClose={handleClose}
        onAdd={handleSuccessNewSupplier}
        onUpdate={handleSuccessUpdateSupplier}
        insideModal={openSelectModal}
        isEdit={isEdit}
        currentSupplier={editingSupplier}
      />
    </div>
  )
}

export default Supplier
